import {
  Column,
  Empty,
  Flex,
  Tabs,
} from '@oliasoft-open-source/react-ui-library';
import {
  Dispatch,
  SetStateAction,
  useEffect, useState,
} from 'react';
import { AvailableViewers } from 'common/enums/known-data-enums';
import { useTranslation } from 'react-i18next';
import translations from 'client/internationalization/translation-map.json';
import { isEmpty } from 'es-toolkit/compat';
import { IObject } from 'common/interfaces/hierarchy-entities';
import { IIPMFile } from 'common/interfaces/settings';
import { ExcelViewer } from './excel-viewer/excel-viewer';
import { JSONViewer } from './json-viewer/json-viewer';
import { PDFViewer } from './pdf-viewer/pdf-viewer';
import { TableViewer } from './table-viewer/table-viewer';
import { MetadataComponent } from '../information-tab/information-tab';
import './data-viewer.css';
import { FileViewer } from './file-viewer/file-viewer';
import { ImageViewer } from './image-viewer/image-viewer';
import { IPMFileViewer } from './ipm-viewer/ipm-viewer';
import { ModalType } from 'common/enums/information-enums';

enum ViewerTypes {
  Json = AvailableViewers.Json,
  Excel = AvailableViewers.Excel,
  Table = AvailableViewers.Table,
  Graph = AvailableViewers.Graph,
  Pdf = AvailableViewers.Pdf,
  Info = 'Info',
  File = 'File',
  Image = AvailableViewers.Image,
  IPMFile = AvailableViewers.IPMFile,
}

type ViewerSelectorProps = {
  viewers: ViewerTypes[]
  selectedViewer: ViewerTypes | undefined
  setViewer: (...args: any[]) => any;
};

const ViewerSelector = ({ viewers, selectedViewer, setViewer }: ViewerSelectorProps) => {
  const { t } = useTranslation();

  const viewerLabelMap = {
    [ViewerTypes.Pdf]: 'PDF',
    [ViewerTypes.Excel]: 'Excel',
    [ViewerTypes.Json]: 'JSON',
    [ViewerTypes.Table]: t(translations.viewer_table),
    [ViewerTypes.Graph]: t(translations.viewer_graph),
    [ViewerTypes.Info]: t(translations.info),
    [ViewerTypes.File]: t(translations.file),
    [ViewerTypes.Image]: t(translations.viewer_image),
    [ViewerTypes.IPMFile]: 'IPM',
  };

  const options = viewers.map((viewer) => ({
    label: viewerLabelMap[viewer],
    value: viewer,
  }));

  const activeTab = options.find((option) => option.value === selectedViewer) || options[0];

  return (
    <Tabs
      options={options}
      value={activeTab}
      onChange={(evt) => {
        setViewer(evt.target.value);
      }}
      padding
    />
  );
};

type DataViewerProps = {
  dataObject: IObject | null;
  width: number | string;
  setSelectedData: Dispatch<SetStateAction<IObject | null>>;
  setSelectedMultipleLinkedItems?: (data: IObject[], modalType: ModalType) => void;
  withPaste: boolean;
};

export const DataViewer = ({
  dataObject,
  width,
  setSelectedData,
  setSelectedMultipleLinkedItems,
  withPaste,
}: DataViewerProps) => {
  const [selectedViewer, setSelectedViewer] = useState<ViewerTypes>();
  const [viewers, setViewers] = useState<ViewerTypes[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    const possibleViewers = dataObject?.metadata?.supportedViewers?.slice() || [];

    if (isEmpty(possibleViewers) && dataObject?.metadata?.blobData) {
      possibleViewers.push(ViewerTypes.File);
    }

    setViewers(possibleViewers);

    if (dataObject) {
      setSelectedViewer(possibleViewers[0]);
    }
  }, [dataObject]);

  const onViewerChange = (viewer: ViewerTypes) => {
    setSelectedViewer(viewer);
  };

  const getViewer = (viewer:ViewerTypes) => {
    if (!viewer || !dataObject) {
      return null;
    }
    switch (viewer) {
      case ViewerTypes.Table:
        return <TableViewer
          dataObject={dataObject}
          key={JSON.stringify(dataObject?.data?.fields)}
          setSelectedData={setSelectedData}
          setSelectedMultipleLinkedItems={setSelectedMultipleLinkedItems}
          withPaste={withPaste}
        />;
      case ViewerTypes.Pdf:
        return <PDFViewer data={dataObject} />;
      case ViewerTypes.Json:
        return <JSONViewer dataObject={dataObject} />;
      case ViewerTypes.Excel:
        return <ExcelViewer dataObject={dataObject} />;
      case ViewerTypes.Info:
        return <MetadataComponent dataObject={dataObject} />;
      case ViewerTypes.File:
        return <FileViewer dataObject={dataObject} />;
      case ViewerTypes.Image:
        return <ImageViewer dataObject={dataObject} />;
      case ViewerTypes.IPMFile: {
        return <IPMFileViewer dataObject={dataObject as unknown as IIPMFile} />;
      }
      default:
        throw new Error(`Not implemented. ${viewer}`);
    }
  };

  return (!dataObject?.data && !dataObject?.metadata)
    ? (
      <div style={{
        padding: 'var(--padding)', width,
      }}
      >
        <Empty height="100%" width="100%" text={t(translations.viewer_noData)} />
      </div>
    )
    : (
      <Column scroll={false}>
        <ViewerSelector viewers={viewers} selectedViewer={selectedViewer} setViewer={onViewerChange} />
        <Flex justifyContent="center">
          <div style={{
            padding: 'var(--padding)', width: width === 'auto' ? width : +width - 32,
          }}
          >
            {getViewer(selectedViewer || viewers[0])}
          </div>
        </Flex>
      </Column>
    );
};
